// src/pages/production/ItemsTab.tsx
//
// Items (raw materials) CRUD. Backed by per-tenant localStorage via
// productionStorage. Recipes reference these by id.

import { useEffect, useMemo, useState } from 'react';
import { Plus, Pencil, Trash2, Search, Package } from 'lucide-react';
import Modal from '../../components/Modal';
import { getItems, saveItem, deleteItem } from '../../services/productionStorage';
import type { ProductionItem } from '../../types/production';

interface ItemsTabProps {
  slug: string;
}

const UNIT_OPTIONS = ['lb', 'oz', 'kg', 'g', 'gal', 'qt', 'pt', 'cup', 'fl oz', 'ml', 'L', 'ea'];

interface EditState {
  id?: string;
  name: string;
  description: string;
  unit: string;
  costPerUnit: string;
  category: string;
  onHand: string;
}

const EMPTY: EditState = { name: '', description: '', unit: 'lb', costPerUnit: '0', category: '', onHand: '' };

export default function ItemsTab({ slug }: ItemsTabProps) {
  const [items, setItems] = useState<ProductionItem[]>([]);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<EditState | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<ProductionItem | null>(null);

  useEffect(() => { setItems(getItems(slug)); }, [slug]);

  const filtered = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter((i) =>
      i.name.toLowerCase().includes(q) ||
      (i.description || '').toLowerCase().includes(q) ||
      (i.category || '').toLowerCase().includes(q),
    );
  }, [items, search]);

  function openNew() { setEditing({ ...EMPTY }); }
  function openEdit(it: ProductionItem) {
    setEditing({
      id: it.id,
      name: it.name,
      description: it.description || '',
      unit: it.unit,
      costPerUnit: String(it.costPerUnit ?? 0),
      category: it.category || '',
      onHand: it.onHand != null ? String(it.onHand) : '',
    });
  }

  function handleSave() {
    if (!editing) return;
    const name = editing.name.trim();
    if (!name) return;
    const cost = parseFloat(editing.costPerUnit);
    const onHand = editing.onHand.trim() ? parseFloat(editing.onHand) : undefined;
    saveItem(slug, {
      id: editing.id,
      name,
      description: editing.description.trim() || undefined,
      unit: editing.unit,
      costPerUnit: Number.isFinite(cost) ? cost : 0,
      category: editing.category.trim() || undefined,
      onHand: Number.isFinite(onHand as number) ? onHand : undefined,
    });
    setItems(getItems(slug));
    setEditing(null);
  }

  function handleDelete() {
    if (!confirmDelete) return;
    deleteItem(slug, confirmDelete.id);
    setItems(getItems(slug));
    setConfirmDelete(null);
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-white">Items</h2>
          <p className="text-sm text-slate-400 mt-0.5">Raw materials and ingredients used by recipes.</p>
        </div>
        <button
          onClick={openNew}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-orange-500 hover:bg-orange-400 text-white font-medium text-sm transition-colors"
        >
          <Plus size={14} />
          New item
        </button>
      </div>

      {/* Search */}
      <div className="mb-4 relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search items by name, description, or category…"
          className="w-full pl-9 pr-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-sm text-white placeholder-slate-500 focus:border-orange-500/50 focus:outline-none"
        />
      </div>

      {/* Table / empty state */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 rounded-2xl bg-slate-800/30 border border-slate-700/40">
          <Package size={32} className="mx-auto text-slate-600 mb-3" />
          <p className="text-slate-400 text-sm">
            {items.length === 0 ? 'No items yet — click "New item" to add one.' : 'No items match your search.'}
          </p>
        </div>
      ) : (
        <div className="rounded-2xl bg-slate-800/30 border border-slate-700/40 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 text-xs uppercase tracking-wider bg-slate-900/40">
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Category</th>
                <th className="px-4 py-3 font-medium text-right">Cost / unit</th>
                <th className="px-4 py-3 font-medium">Unit</th>
                <th className="px-4 py-3 font-medium w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/40">
              {filtered.map((i) => (
                <tr key={i.id} className="hover:bg-slate-700/20">
                  <td className="px-4 py-3">
                    <div className="text-white font-medium">{i.name}</div>
                    {i.description && (
                      <div className="text-xs text-slate-500 mt-0.5">{i.description}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-300">{i.category || '—'}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-200">
                    ${i.costPerUnit.toFixed(4)}
                  </td>
                  <td className="px-4 py-3 text-slate-400">{i.unit}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openEdit(i)}
                        className="p-1.5 rounded text-slate-400 hover:text-blue-400 hover:bg-blue-500/10"
                        title="Edit"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => setConfirmDelete(i)}
                        className="p-1.5 rounded text-slate-400 hover:text-red-400 hover:bg-red-500/10"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit / Create modal */}
      {editing && (
        <Modal onClose={() => setEditing(null)} panelClassName="max-w-xl">
          <h3 className="text-lg font-semibold text-white mb-4">
            {editing.id ? 'Edit item' : 'New item'}
          </h3>
          <div className="space-y-3">
            <Field label="Name">
              <input
                autoFocus
                type="text"
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-sm text-white placeholder-slate-500 focus:border-orange-500/50 focus:outline-none"
              />
            </Field>
            <Field label="Description">
              <input
                type="text"
                value={editing.description}
                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-sm text-white placeholder-slate-500 focus:border-orange-500/50 focus:outline-none"
              />
            </Field>
            <div className="grid grid-cols-4 gap-3">
              <Field label="Category">
                <input
                  type="text"
                  value={editing.category}
                  onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-sm text-white placeholder-slate-500 focus:border-orange-500/50 focus:outline-none"
                  placeholder="e.g. Produce"
                />
              </Field>
              <Field label="Cost / unit">
                <input
                  type="number"
                  step="0.0001"
                  min="0"
                  value={editing.costPerUnit}
                  onChange={(e) => setEditing({ ...editing, costPerUnit: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-sm text-white placeholder-slate-500 focus:border-orange-500/50 focus:outline-none font-mono"
                />
              </Field>
              <Field label="Unit">
                <select
                  value={editing.unit}
                  onChange={(e) => setEditing({ ...editing, unit: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-sm text-white placeholder-slate-500 focus:border-orange-500/50 focus:outline-none"
                >
                  {UNIT_OPTIONS.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </Field>
              <Field label="On hand (optional)">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editing.onHand}
                  onChange={(e) => setEditing({ ...editing, onHand: e.target.value })}
                  placeholder="—"
                  className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-sm text-white placeholder-slate-500 focus:border-orange-500/50 focus:outline-none font-mono"
                />
              </Field>
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <button
              onClick={() => setEditing(null)}
              className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!editing.name.trim()}
              className="px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-400 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium"
            >
              {editing.id ? 'Save changes' : 'Create item'}
            </button>
          </div>
        </Modal>
      )}

      {/* Delete confirm */}
      {confirmDelete && (
        <Modal onClose={() => setConfirmDelete(null)} panelClassName="max-w-sm">
          <h3 className="text-lg font-semibold text-white mb-2">Delete item?</h3>
          <p className="text-sm text-slate-400 mb-6">
            Delete <span className="text-white font-medium">{confirmDelete.name}</span>?
            Recipes that reference it will show the line as missing.
          </p>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setConfirmDelete(null)}
              className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              className="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-400 text-white text-sm font-medium"
            >
              Delete
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-400 mb-1">{label}</span>
      {children}
    </label>
  );
}
